import { formatElapsedTime } from '../../utils/timeUtils'
import { type ColumnPlacement, ColumnWriter } from './columnWriter'
import { LapsFormatError } from './lapsFormatError'

export type FieldKind =
  | 'number'
  | 'date'
  | 'boolean'
  | 'text'
  | 'elapsedTimeString'

export interface FieldSpec {
  readonly name: string
  readonly kind: FieldKind
}

export type ColumnType = 'f64' | 'u8'

export interface StoredColumnSpec extends ColumnPlacement {
  field: string
  role: 'value' | 'presence'
  type: ColumnType
  values?: string[]
}

export interface DerivedColumnSpec {
  field: string
  role: 'derived'
}

export type ColumnSpec = StoredColumnSpec | DerivedColumnSpec

export interface TableLayout {
  rows: number
  columns: ColumnSpec[]
}

type Row = Record<string, unknown>

const ELAPSED_TIME_STRING_SOURCE = 'elapsedMs'
const MAX_TEXTS = 256
const BYTES_PER_TYPE: Record<ColumnType, number> = { f64: 8, u8: 1 }

const ABSENT = 0
const PRESENT = 1
const NULL = 2

export function encodeTable(
  table: string,
  rows: readonly object[],
  fields: readonly FieldSpec[],
  writer: ColumnWriter
): TableLayout {
  const typedRows = rows as readonly Row[]
  assertEveryKeyHasAField(table, typedRows, fields)
  const columns = fields.flatMap((field) =>
    encodeField(`${table}.${field.name}`, typedRows, field, writer)
  )
  return { rows: rows.length, columns }
}

export function decodeTable(
  layout: TableLayout,
  fields: readonly FieldSpec[],
  data: ArrayBuffer
): Row[] {
  const rows: Row[] = Array.from({ length: layout.rows }, () => ({}))
  const stored = fields.filter((field) => field.kind !== 'elapsedTimeString')
  const derived = fields.filter((field) => field.kind === 'elapsedTimeString')
  for (const field of [...stored, ...derived]) {
    decodeField(layout, field, data, rows)
  }
  return rows
}

function assertEveryKeyHasAField(
  table: string,
  rows: readonly Row[],
  fields: readonly FieldSpec[]
): void {
  const known = new Set(fields.map((field) => field.name))
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!known.has(key)) {
        throw new LapsFormatError(`${table}.${key} has no column`)
      }
    }
  }
}

function encodeField(
  path: string,
  rows: readonly Row[],
  field: FieldSpec,
  writer: ColumnWriter
): ColumnSpec[] {
  const states = presenceStates(rows, field.name)
  const present = states.filter((state) => state === PRESENT).length
  const anyStored = states.some((state) => state !== ABSENT)
  if (!anyStored) return []
  const columns: ColumnSpec[] = []
  if (present !== rows.length) {
    columns.push(stored(field.name, 'presence', 'u8', writer.add(states)))
  }
  if (present > 0) columns.push(valueColumn(path, rows, field, states, writer))
  return columns
}

function presenceStates(rows: readonly Row[], name: string): Uint8Array {
  const states = new Uint8Array(rows.length)
  rows.forEach((row, i) => {
    const value = row[name]
    states[i] = value === undefined ? ABSENT : value === null ? NULL : PRESENT
  })
  return states
}

function valueColumn(
  path: string,
  rows: readonly Row[],
  field: FieldSpec,
  states: Uint8Array,
  writer: ColumnWriter
): ColumnSpec {
  const presentValues = (each: (value: unknown, row: Row, i: number) => void) =>
    rows.forEach((row, i) => {
      if (states[i] === PRESENT) each(row[field.name], row, i)
    })
  switch (field.kind) {
    case 'number': {
      const values = new Float64Array(rows.length)
      presentValues((value, _row, i) => {
        if (typeof value !== 'number') fail(path, 'is not a number')
        values[i] = value as number
      })
      return stored(field.name, 'value', 'f64', writer.add(values))
    }
    case 'date': {
      const values = new Float64Array(rows.length)
      presentValues((value, _row, i) => {
        if (!(value instanceof Date)) fail(path, 'is not a Date')
        values[i] = (value as Date).getTime()
      })
      return stored(field.name, 'value', 'f64', writer.add(values))
    }
    case 'boolean': {
      const values = new Uint8Array(rows.length)
      presentValues((value, _row, i) => {
        if (typeof value !== 'boolean') fail(path, 'is not a boolean')
        values[i] = value ? 1 : 0
      })
      return stored(field.name, 'value', 'u8', writer.add(values))
    }
    case 'text':
      return textColumn(path, rows, field, states, writer)
    case 'elapsedTimeString':
      presentValues((value, row) => {
        if (value !== elapsedTimeString(row)) {
          fail(path, 'does not match formatElapsedTime(elapsedMs)')
        }
      })
      return { field: field.name, role: 'derived' }
  }
}

function textColumn(
  path: string,
  rows: readonly Row[],
  field: FieldSpec,
  states: Uint8Array,
  writer: ColumnWriter
): ColumnSpec {
  const dictionary = new Map<string, number>()
  const indexes = new Uint8Array(rows.length)
  rows.forEach((row, i) => {
    if (states[i] !== PRESENT) return
    const value = row[field.name]
    if (typeof value !== 'string') fail(path, 'is not a text')
    let index = dictionary.get(value as string)
    if (index === undefined) {
      index = dictionary.size
      if (index >= MAX_TEXTS) {
        fail(path, `has more than ${MAX_TEXTS} different texts`)
      }
      dictionary.set(value as string, index)
    }
    indexes[i] = index
  })
  return {
    ...stored(field.name, 'value', 'u8', writer.add(indexes)),
    values: Array.from(dictionary.keys()),
  }
}

function stored(
  field: string,
  role: StoredColumnSpec['role'],
  type: ColumnType,
  placement: ColumnPlacement
): StoredColumnSpec {
  return { field, role, type, ...placement }
}

function elapsedTimeString(row: Row): string {
  return formatElapsedTime(row[ELAPSED_TIME_STRING_SOURCE] as number)
}

function decodeField(
  layout: TableLayout,
  field: FieldSpec,
  data: ArrayBuffer,
  rows: Row[]
): void {
  const specs = layout.columns.filter((spec) => spec.field === field.name)
  if (specs.length === 0) return
  const presence = specs.find(
    (spec): spec is StoredColumnSpec => spec.role === 'presence'
  )
  const value = specs.find((spec) => spec.role !== 'presence')
  const states = presence ? view(data, presence) : undefined
  const read = value ? reader(field, value, data) : undefined
  rows.forEach((row, i) => {
    const state = states ? states[i] : PRESENT
    if (state === NULL) row[field.name] = null
    if (state === PRESENT && read) row[field.name] = read(i, row)
  })
}

function reader(
  field: FieldSpec,
  spec: ColumnSpec,
  data: ArrayBuffer
): (i: number, row: Row) => unknown {
  if (spec.role === 'derived') return (_i, row) => elapsedTimeString(row)
  const values = view(data, spec)
  switch (field.kind) {
    case 'number':
      return (i) => values[i]
    case 'date':
      return (i) => new Date(values[i])
    case 'boolean':
      return (i) => values[i] === 1
    case 'text': {
      const texts = spec.values ?? []
      return (i) => texts[values[i]]
    }
    case 'elapsedTimeString':
      return (_i, row) => elapsedTimeString(row)
  }
}

function view(
  data: ArrayBuffer,
  spec: StoredColumnSpec
): Float64Array | Uint8Array {
  const end = spec.offset + spec.length * BYTES_PER_TYPE[spec.type]
  if (end > data.byteLength) {
    throw new LapsFormatError(
      `the ${spec.field} column runs past the end of the file`
    )
  }
  return spec.type === 'f64'
    ? new Float64Array(data, spec.offset, spec.length)
    : new Uint8Array(data, spec.offset, spec.length)
}

function fail(path: string, problem: string): never {
  throw new LapsFormatError(`${path} ${problem}`)
}
